import { create } from 'zustand'
import { countLedgerRowsWithRedStatus } from '@/components/ledgerFilters'
import {
  countBillingUploadsErrors,
  countProductUploadsErrors,
  countQboUploadsErrors,
  countWhUploadsErrors,
  sumUploadsErrorCounts,
} from '@/components/uploadsErrorCounts'
import {
  getWarehouseBillingAwareness,
  getWarehouseCoverage,
  getWarehouseNavStatus,
  listWarehouseBillingLines,
  listWarehouseInvoices,
  listWarehouseLedger,
  listWarehouseMovements,
} from '@/services/warehouse'
import type { WarehouseBillingLineItem } from '@/services/warehouseTypes'
import { uploadsErrorVisibleForMonth } from '@/utils/completedCalendarMonth'

interface WarehouseProblemState {
  problemCount: number
  ledgerErrorCount: number
  uploadsErrorCount: number
  setProblemCount: (count: number) => void
  setUploadsErrorCount: (count: number) => void
  fetchProblemCount: () => Promise<void>
  fetchLedgerErrorCount: () => Promise<void>
  fetchUploadsErrorCount: () => Promise<void>
  fetchNavStatus: () => Promise<void>
}

async function fetchBillingQtyErrorCount(
  billingLines: WarehouseBillingLineItem[]
): Promise<number> {
  const months = Array.from(new Set(billingLines.map((line) => line.report_month))).sort()
  if (months.length === 0) return 0
  const results = await Promise.all(months.map((month) => getWarehouseBillingAwareness(month)))
  return results.reduce(
    (sum, result) =>
      uploadsErrorVisibleForMonth(result.report_month || null)
        ? sum + result.mismatch_count
        : sum,
    0
  )
}

export const useWarehouseProblemStore = create<WarehouseProblemState>((set) => ({
  problemCount: 0,
  ledgerErrorCount: 0,
  uploadsErrorCount: 0,
  setProblemCount: (problemCount) => set({ problemCount }),
  setUploadsErrorCount: (uploadsErrorCount) => set({ uploadsErrorCount }),
  fetchProblemCount: async () => {
    try {
      const movements = await listWarehouseMovements()
      const problemCount = movements.filter((move) => Boolean(move.exception?.trim())).length
      set({ problemCount })
    } catch (err) {
      console.error('Failed to fetch warehouse problem count:', err)
    }
  },
  fetchLedgerErrorCount: async () => {
    try {
      const response = await listWarehouseLedger({ limit: 2000 })
      set({ ledgerErrorCount: countLedgerRowsWithRedStatus(response.items) })
    } catch (err) {
      console.error('Failed to fetch warehouse ledger error count:', err)
    }
  },
  fetchUploadsErrorCount: async () => {
    try {
      const [invoices, billingLines, coverage] = await Promise.all([
        listWarehouseInvoices(),
        listWarehouseBillingLines(),
        getWarehouseCoverage(),
      ])
      const qboMatches = coverage.qbo_bill_matches ?? []
      const billingQtyErrorCount = await fetchBillingQtyErrorCount(billingLines)
      const uploadsErrorCount = sumUploadsErrorCounts({
        wh: countWhUploadsErrors(invoices, billingLines),
        product: countProductUploadsErrors(invoices),
        billing: countBillingUploadsErrors(billingLines, billingQtyErrorCount),
        qbo: countQboUploadsErrors(qboMatches),
      })
      set({ uploadsErrorCount })
    } catch (err) {
      console.error('Failed to fetch warehouse uploads error count:', err)
    }
  },
  fetchNavStatus: async () => {
    try {
      const status = await getWarehouseNavStatus()
      set({
        ledgerErrorCount: status.ledger_error_count,
        uploadsErrorCount: status.uploads_error_count,
      })
    } catch (err) {
      console.error('Failed to fetch warehouse nav status:', err)
    }
  },
}))
